import * as RTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { directionFor } from '@/lib/i18n/config';
import { cn } from './cn';

export interface TabDef {
  value: string;
  label: ReactNode;
  content: ReactNode;
}

export function Tabs({
  tabs,
  value,
  onValueChange,
  defaultValue,
}: {
  tabs: TabDef[];
  value?: string;
  onValueChange?: (v: string) => void;
  defaultValue?: string;
}) {
  /*
   * Radix defaults `dir` to `"ltr"` and puts it on the DOM as a real attribute, so an
   * un-told Tabs does not merely fail to reverse its own strip — it forces every
   * descendant back to LTR, whatever `<html dir="rtl">` says. That is the whole tab
   * PANEL, not just the triggers: in Arabic the settings tabs read left-to-right and
   * each tab's content sits against the left edge of a right-to-left page.
   *
   * `useTranslation` rather than reading `document.documentElement.dir`: it subscribes
   * to the language, so the header's EN/AR toggle turns the panel round on the click
   * instead of on the next unrelated re-render.
   */
  const { i18n } = useTranslation();
  const dir = directionFor(i18n.language);

  return (
    <RTabs.Root
      dir={dir}
      value={value}
      onValueChange={onValueChange}
      defaultValue={defaultValue ?? tabs[0]?.value}
    >
      {/* The strip scrolls inside itself rather than pushing the page wider: a
          seven-tab settings page at 768px used to make the WHOLE document scroll
          sideways, which drags the forms and tables under it off screen too. The
          triggers keep their own width so the labels are never truncated, and
          Radix moves focus with the arrow keys, which scrolls the focused tab
          into view. */}
      <RTabs.List className="flex gap-lg overflow-x-auto border-b-[0.5px] border-hairline">
        {tabs.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            className={cn(
              'relative mb-[-0.5px] shrink-0 whitespace-nowrap pb-sm pt-xs text-small font-medium text-ink-mid outline-hidden',
              'data-[state=active]:text-accent-blue data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:bottom-0 data-[state=active]:after:h-[2px] data-[state=active]:after:bg-accent-blue',
              'focus-visible:shadow-focus',
            )}
          >
            {t.label}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {tabs.map((t) => (
        <RTabs.Content key={t.value} value={t.value} className="pt-lg focus-visible:outline-hidden">
          {t.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}
